import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Plus,
  Check,
  Trash2,
  Edit3,
  Bot,
  Search,
  Zap,
  Users,
  MessageSquare,
  GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useSkillPacks,
  useCreateSkillPack,
  useUpdateSkillPack,
  useDeleteSkillPack,
  useAssignSkillPack,
  useSessionPackAssignments,
  SkillPack,
  PackAssignment,
} from '../helpers/useSkillPacks';
import { loadInstalledSkills, saveInstalledSkills, InstalledSkill } from './SkillSearchModal';
import styles from './SkillTreeModal.module.css';

const PACK_COLORS = [
  '#6366f1',
  '#a855f7',
  '#ec4899',
  '#f43f5e',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
];

const PACK_ICONS = ['🎯', '🚀', '🔧', '🎨', '🛡️', '⚡', '🧩', '🌐', '📦', '🤖', '💎', '🔥'];

interface SkillTreeModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  initialMode?: 'view' | 'create';
  participants?: Array<{ personaId: string; personaName: string; personaIcon?: string }>;
}

type PanelMode = 'none' | 'create' | 'edit' | 'detail';

// Fetch SKILL.md from GitHub repos (simplified version of SkillSearchModal logic)
async function fetchSkillMd(fullName: string, skillName: string): Promise<string | undefined> {
  const paths = [
    `${skillName}/SKILL.md`,
    `${skillName}/skill.md`,
    `skills/${skillName}/SKILL.md`,
    'SKILL.md',
  ];
  for (const p of paths) {
    try {
      const res = await fetch(`https://raw.githubusercontent.com/${fullName}/main/${p}`);
      if (res.ok) return await res.text();
    } catch {
      /* next path */
    }
  }
  return undefined;
}

export const SkillTreeModal: React.FC<SkillTreeModalProps> = ({
  open,
  onClose,
  sessionId,
  initialMode = 'view',
  participants = [],
}) => {
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const { data: packs = [], isLoading: packsLoading } = useSkillPacks(open);
  const { data: assignments = [] } = useSessionPackAssignments(sessionId, open);
  const createPack = useCreateSkillPack();
  const updatePack = useUpdateSkillPack();
  const deletePack = useDeleteSkillPack();
  const assignPack = useAssignSkillPack();

  const [panelMode, setPanelMode] = useState<PanelMode>('none');
  const [selectedPack, setSelectedPack] = useState<SkillPack | null>(null);
  const [installedSkills, setInstalledSkills] = useState<InstalledSkill[]>([]);
  const [hoveredPack, setHoveredPack] = useState<number | null>(null);
  const [hoveredTarget, setHoveredTarget] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formIcon, setFormIcon] = useState('🎯');
  const [formColor, setFormColor] = useState('#6366f1');
  const [formSelectedSkills, setFormSelectedSkills] = useState<Set<string>>(new Set());
  const [assignTarget, setAssignTarget] = useState<string>('__main__');

  // Inline skill search
  const [skillQuery, setSkillQuery] = useState('');
  const [searchResults, setSearchResults] = useState<
    Array<{ name: string; fullName: string; description: string; url: string }>
  >([]);
  const [isSearching, setIsSearching] = useState(false);

  // Refs for SVG line drawing
  const canvasRef = useRef<HTMLDivElement>(null);
  const skillRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const packRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const targetRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    setInstalledSkills(loadInstalledSkills());
  }, [open]);

  useEffect(() => {
    if (initialMode === 'create' && open) {
      setPanelMode('create');
    }
  }, [initialMode, open]);

  // Force re-render after mount to draw SVG lines once refs are populated
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => forceUpdate((n) => n + 1), 100);
      return () => clearTimeout(timer);
    }
  }, [open, packs, assignments, installedSkills]);

  // Build assignment lookup
  const assignmentsByTarget = useMemo(() => {
    const map = new Map<string, PackAssignment[]>();
    for (const a of assignments) {
      const key = a.personaId ?? '__main__';
      const arr = map.get(key) || [];
      arr.push(a);
      map.set(key, arr);
    }
    return map;
  }, [assignments]);

  // Skill search handler
  const handleSearch = useCallback(async () => {
    if (!skillQuery.trim()) return;
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://api.github.com/search/repositories?q=${encodeURIComponent(skillQuery + ' skill agent')}+in:name,description&sort=stars&per_page=8`,
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(
          (data.items || []).map((r: any) => ({
            name: r.name,
            fullName: r.full_name,
            description: r.description || '',
            url: r.html_url,
          })),
        );
      }
    } catch {
      toast.error('Search failed');
    }
    setIsSearching(false);
  }, [skillQuery]);

  // Quick install skill from search
  const handleQuickInstall = async (result: {
    name: string;
    fullName: string;
    description: string;
    url: string;
  }) => {
    const existing = installedSkills.find((s) => s.fullName === result.fullName);
    if (existing) {
      toast.info(`${result.name} is already installed`);
      return;
    }

    const content = await fetchSkillMd(result.fullName, result.name);
    const newSkill: InstalledSkill = {
      name: result.name,
      fullName: result.fullName,
      description: result.description,
      url: result.url,
      active: true,
      installedAt: new Date().toISOString(),
      content,
    };
    const updated = [...installedSkills, newSkill];
    setInstalledSkills(updated);
    saveInstalledSkills(updated);
    toast.success(`Installed "${result.name}"`);
  };

  const handleCreatePack = async () => {
    if (!formName.trim()) {
      toast.error('Pack name is required');
      return;
    }
    const skills = installedSkills
      .filter((s) => formSelectedSkills.has(s.fullName))
      .map((s) => ({
        skillName: s.name,
        skillFullName: s.fullName,
        skillDescription: s.description,
        skillUrl: s.url,
      }));

    if (skills.length === 0) {
      toast.error('Select at least one skill');
      return;
    }

    try {
      await createPack.mutateAsync({
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        icon: formIcon,
        color: formColor,
        skills,
      });
      toast.success(`Pack "${formName}" created`);
      resetForm();
      setPanelMode('none');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create pack');
    }
  };

  const handleUpdatePack = async () => {
    if (!selectedPack || !formName.trim()) return;
    const skills = installedSkills
      .filter((s) => formSelectedSkills.has(s.fullName))
      .map((s) => ({
        skillName: s.name,
        skillFullName: s.fullName,
        skillDescription: s.description,
        skillUrl: s.url,
      }));

    try {
      await updatePack.mutateAsync({
        packId: selectedPack.id,
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        icon: formIcon,
        color: formColor,
        skills,
      });
      toast.success('Pack updated');
      setPanelMode('none');
      setSelectedPack(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleDeletePack = async (packId: number) => {
    try {
      await deletePack.mutateAsync(packId);
      toast.success('Pack deleted');
      setPanelMode('none');
      setSelectedPack(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleAssign = async (packId: number, personaId: string | null) => {
    try {
      await assignPack.mutateAsync({ packId, sessionId, personaId, action: 'assign' });
      toast.success('Pack assigned');
    } catch (err: any) {
      toast.error(err.message || 'Failed to assign');
    }
  };

  const handleUnassign = async (packId: number, personaId: string | null) => {
    try {
      await assignPack.mutateAsync({ packId, sessionId, personaId, action: 'unassign' });
      toast.success('Pack unassigned');
    } catch (err: any) {
      toast.error(err.message || 'Failed to unassign');
    }
  };

  const openPackDetail = (pack: SkillPack) => {
    setSelectedPack(pack);
    setPanelMode('detail');
  };

  const openEditPack = (pack: SkillPack) => {
    setSelectedPack(pack);
    setFormName(pack.name);
    setFormDesc(pack.description || '');
    setFormIcon(pack.icon);
    setFormColor(pack.color);
    setFormSelectedSkills(new Set(pack.skills.map((s) => s.skillFullName)));
    setPanelMode('edit');
  };

  const resetForm = () => {
    setFormName('');
    setFormDesc('');
    setFormIcon('🎯');
    setFormColor('#6366f1');
    setFormSelectedSkills(new Set());
    setAssignTarget('__main__');
  };

  const openCreatePanel = () => {
    resetForm();
    setPanelMode('create');
  };

  const toggleSkillSelection = (fullName: string) => {
    setFormSelectedSkills((prev) => {
      const next = new Set(prev);
      if (next.has(fullName)) next.delete(fullName);
      else next.add(fullName);
      return next;
    });
  };

  // All targets: Main AI, participants, TaskingBot, Workflows
  const targets = useMemo(() => {
    const t: Array<{ id: string; name: string; icon: React.ReactNode; label?: string }> = [
      { id: '__main__', name: 'Main AI', icon: <Bot size={16} />, label: 'Lead' },
    ];
    for (const p of participants) {
      t.push({ id: p.personaId, name: p.personaName, icon: <span>{p.personaIcon || '🤖'}</span> });
    }
    t.push(
      { id: '__taskingbot__', name: 'TaskingBot', icon: <MessageSquare size={16} />, label: 'Bot' },
      { id: '__workflows__', name: 'Workflows', icon: <GitBranch size={16} />, label: 'Auto' },
    );
    return t;
  }, [participants]);

  // Build skill-to-pack mapping for mind-map lines
  const skillToPackMap = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const pack of packs) {
      for (const s of pack.skills) {
        const arr = map.get(s.skillFullName) || [];
        arr.push(pack.id);
        map.set(s.skillFullName, arr);
      }
    }
    return map;
  }, [packs]);

  // Compute SVG connection lines
  const connectionLines = useMemo(() => {
    const lines: Array<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      color: string;
      type: 'skill-pack' | 'pack-target';
      highlighted: boolean;
    }> = [];
    const canvas = canvasRef.current;
    if (!canvas) return lines;
    const canvasRect = canvas.getBoundingClientRect();

    // Skill → Pack lines
    for (const pack of packs) {
      const packEl = packRefs.current.get(pack.id);
      if (!packEl) continue;
      const packRect = packEl.getBoundingClientRect();
      const px = packRect.left - canvasRect.left;
      const py = packRect.top + packRect.height / 2 - canvasRect.top;

      for (const s of pack.skills) {
        const skillEl = skillRefs.current.get(s.skillFullName);
        if (!skillEl) continue;
        const skillRect = skillEl.getBoundingClientRect();
        const sx = skillRect.right - canvasRect.left;
        const sy = skillRect.top + skillRect.height / 2 - canvasRect.top;

        const highlighted = hoveredPack === pack.id;
        lines.push({
          x1: sx,
          y1: sy,
          x2: px,
          y2: py,
          color: pack.color,
          type: 'skill-pack',
          highlighted,
        });
      }
    }

    // Pack → Target lines
    for (const a of assignments) {
      const packEl = packRefs.current.get(a.packId);
      const targetKey = a.personaId ?? '__main__';
      const targetEl = targetRefs.current.get(targetKey);
      if (!packEl || !targetEl) continue;

      const packRect = packEl.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      const px = packRect.right - canvasRect.left;
      const py = packRect.top + packRect.height / 2 - canvasRect.top;
      const tx = targetRect.left - canvasRect.left;
      const ty = targetRect.top + targetRect.height / 2 - canvasRect.top;

      const pack = packs.find((p) => p.id === a.packId);
      const highlighted = hoveredPack === a.packId || hoveredTarget === targetKey;
      lines.push({
        x1: px,
        y1: py,
        x2: tx,
        y2: ty,
        color: pack?.color || '#f97316',
        type: 'pack-target',
        highlighted,
      });
    }

    return lines;
  }, [packs, assignments, hoveredPack, hoveredTarget, canvasRef.current]);

  if (!open) return null;

  const modalContent = (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIcon}>🌳</span>
            <span className={styles.headerTitle}>SKILL TREE</span>
            <span className={styles.headerSubtitle}>
              {installedSkills.length} skills &middot; {packs.length} packs &middot;{' '}
              {assignments.length} assignments
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className={styles.btnPrimary}
              style={{ width: 'auto', padding: '6px 16px', marginTop: 0, fontSize: 12 }}
              onClick={openCreatePanel}
            >
              <Plus size={14} style={{ marginRight: 4 }} />
              New Pack
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body: Mind Map */}
        <div className={styles.body}>
          <div className={styles.mindMap} ref={canvasRef}>
            {/* SVG Connection Lines */}
            <svg className={styles.svgLayer}>
              {connectionLines.map((line, i) => {
                const dx = line.x2 - line.x1;
                const cp = Math.abs(dx) * 0.45;
                return (
                  <path
                    key={i}
                    d={`M ${line.x1} ${line.y1} C ${line.x1 + cp} ${line.y1}, ${line.x2 - cp} ${line.y2}, ${line.x2} ${line.y2}`}
                    stroke={line.color}
                    strokeWidth={line.highlighted ? 2.5 : 1.2}
                    strokeOpacity={line.highlighted ? 0.7 : 0.2}
                    fill="none"
                  />
                );
              })}
            </svg>

            {/* Column 1: Skills */}
            <div className={styles.mapColumn}>
              <div className={styles.columnLabel}>
                <Zap size={12} /> INSTALLED SKILLS
              </div>
              {installedSkills.map((skill) => {
                const connected = skillToPackMap.has(skill.fullName);
                return (
                  <div
                    key={skill.fullName}
                    ref={(el) => {
                      if (el) skillRefs.current.set(skill.fullName, el);
                    }}
                    className={`${styles.skillNode} ${connected ? styles.skillNodeConnected : ''}`}
                  >
                    <span
                      className={styles.skillDot}
                      style={{ background: connected ? '#22c55e' : '#475569' }}
                    />
                    <div className={styles.skillInfo}>
                      <span className={styles.skillNodeName}>{skill.name}</span>
                      <span className={styles.skillNodeSource}>{skill.fullName}</span>
                    </div>
                  </div>
                );
              })}

              {/* Inline Skill Search */}
              <div className={styles.inlineSearch}>
                <div className={styles.searchRow}>
                  <Search size={13} className={styles.searchIcon} />
                  <input
                    className={styles.searchInput}
                    value={skillQuery}
                    onChange={(e) => setSkillQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search & add skills..."
                  />
                  {skillQuery && (
                    <button
                      className={styles.searchBtn}
                      onClick={handleSearch}
                      disabled={isSearching}
                    >
                      {isSearching ? '...' : 'Go'}
                    </button>
                  )}
                </div>
                {searchResults.length > 0 && (
                  <div className={styles.searchResults}>
                    {searchResults.map((r) => {
                      const alreadyInstalled = installedSkills.some(
                        (s) => s.fullName === r.fullName,
                      );
                      return (
                        <div key={r.fullName} className={styles.searchResultItem}>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div className={styles.searchResultName}>{r.name}</div>
                            <div className={styles.searchResultDesc}>{r.description}</div>
                          </div>
                          <button
                            className={
                              alreadyInstalled
                                ? styles.searchResultInstalled
                                : styles.searchResultAddBtn
                            }
                            onClick={() => !alreadyInstalled && handleQuickInstall(r)}
                            disabled={alreadyInstalled}
                          >
                            {alreadyInstalled ? <Check size={12} /> : <Plus size={12} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Packs */}
            <div className={styles.mapColumn}>
              <div className={styles.columnLabel}>
                <span>📦</span> SKILL PACKS
              </div>
              {packs.map((pack) => {
                const isAssigned = assignments.some((a) => a.packId === pack.id);
                return (
                  <div
                    key={pack.id}
                    ref={(el) => {
                      if (el) packRefs.current.set(pack.id, el);
                    }}
                    className={`${styles.packNode} ${isAssigned ? styles.packNodeActive : ''}`}
                    style={{ '--pack-color': pack.color } as React.CSSProperties}
                    onClick={() => openPackDetail(pack)}
                    onMouseEnter={() => setHoveredPack(pack.id)}
                    onMouseLeave={() => setHoveredPack(null)}
                  >
                    <span className={styles.packIcon}>{pack.icon}</span>
                    <div className={styles.packInfo}>
                      <span className={styles.packName}>{pack.name}</span>
                      <span className={styles.packSkillCount}>
                        {pack.skills.length} skill{pack.skills.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className={styles.packActions}>
                      <button
                        className={styles.packEditBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditPack(pack);
                        }}
                        title="Edit"
                      >
                        <Edit3 size={11} />
                      </button>
                      <button
                        className={styles.packDeleteBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePack(pack.id);
                        }}
                        title="Delete"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {packs.length === 0 && !packsLoading && (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>📦</span>
                  <span className={styles.emptyTitle}>No packs yet</span>
                  <span className={styles.emptyDesc}>
                    Bundle skills into packs, then assign them to targets
                  </span>
                </div>
              )}
              <div className={styles.addPackNode} onClick={openCreatePanel}>
                <Plus size={16} />
                <span>Create Pack</span>
              </div>
            </div>

            {/* Column 3: Targets */}
            <div className={styles.mapColumn}>
              <div className={styles.columnLabel}>
                <Users size={12} /> ASSIGN TO
              </div>
              {targets.map((target) => {
                const targetAssignments = assignmentsByTarget.get(target.id) || [];
                return (
                  <div
                    key={target.id}
                    ref={(el) => {
                      if (el) targetRefs.current.set(target.id, el);
                    }}
                    className={`${styles.targetNode} ${targetAssignments.length > 0 ? styles.targetNodeActive : ''}`}
                    onMouseEnter={() => setHoveredTarget(target.id)}
                    onMouseLeave={() => setHoveredTarget(null)}
                  >
                    <div className={styles.targetIcon}>{target.icon}</div>
                    <div className={styles.targetInfo}>
                      <span className={styles.targetName}>{target.name}</span>
                      {target.label && <span className={styles.targetLabel}>{target.label}</span>}
                    </div>
                    <span className={styles.targetAssignCount}>
                      {targetAssignments.length > 0
                        ? `${targetAssignments.length} pack${targetAssignments.length !== 1 ? 's' : ''}`
                        : 'none'}
                    </span>

                    {/* Drop zone: click any pack to assign to this target */}
                    {packs.length > 0 && (
                      <div className={styles.targetDropArea}>
                        {packs.map((pack) => {
                          const isAlreadyAssigned = targetAssignments.some(
                            (a) => a.packId === pack.id,
                          );
                          return (
                            <button
                              key={pack.id}
                              className={`${styles.targetPackChip} ${isAlreadyAssigned ? styles.targetPackChipActive : ''}`}
                              style={{ '--pack-color': pack.color } as React.CSSProperties}
                              onClick={() => {
                                const personaId =
                                  target.id === '__main__' ||
                                  target.id === '__taskingbot__' ||
                                  target.id === '__workflows__'
                                    ? null
                                    : target.id;
                                if (isAlreadyAssigned) {
                                  handleUnassign(pack.id, personaId);
                                } else {
                                  handleAssign(pack.id, personaId);
                                }
                              }}
                              title={
                                isAlreadyAssigned
                                  ? `Unassign "${pack.name}"`
                                  : `Assign "${pack.name}"`
                              }
                            >
                              <span>{pack.icon}</span>
                              {isAlreadyAssigned && <X size={8} className={styles.chipUnassign} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Side Panel */}
          {panelMode !== 'none' && (
            <div className={styles.sidePanel}>
              <div className={styles.sidePanelHeader}>
                <span className={styles.sidePanelTitle}>
                  {panelMode === 'create'
                    ? 'Create Pack'
                    : panelMode === 'edit'
                      ? 'Edit Pack'
                      : selectedPack?.name || 'Pack Details'}
                </span>
                <button
                  className={styles.closeButton}
                  onClick={() => {
                    setPanelMode('none');
                    setSelectedPack(null);
                  }}
                >
                  <X size={14} />
                </button>
              </div>

              <div className={styles.sidePanelBody}>
                {/* Detail mode */}
                {panelMode === 'detail' && selectedPack && (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <span style={{ fontSize: 36 }}>{selectedPack.icon}</span>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: 'var(--foreground, #e2e8f0)',
                          marginTop: 8,
                        }}
                      >
                        {selectedPack.name}
                      </div>
                      {selectedPack.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--muted-foreground, #94a3b8)',
                            marginTop: 4,
                          }}
                        >
                          {selectedPack.description}
                        </div>
                      )}
                    </div>

                    <div className={styles.formLabel}>Skills ({selectedPack.skills.length})</div>
                    <div className={styles.packDetailSkills}>
                      {selectedPack.skills.map((s) => (
                        <div key={s.id} className={styles.packDetailSkillItem}>
                          <div
                            className={styles.packDetailSkillDot}
                            style={{ '--pack-color': selectedPack.color } as React.CSSProperties}
                          />
                          <span className={styles.packDetailSkillName}>{s.skillName}</span>
                        </div>
                      ))}
                    </div>

                    <div className={styles.formLabel} style={{ marginTop: 16 }}>
                      Assigned To
                    </div>
                    <div className={styles.packDetailSkills}>
                      {assignments
                        .filter((a) => a.packId === selectedPack.id)
                        .map((a) => (
                          <div key={a.assignmentId} className={styles.packDetailSkillItem}>
                            <div
                              className={styles.packDetailSkillDot}
                              style={{ '--pack-color': selectedPack.color } as React.CSSProperties}
                            />
                            <span className={styles.packDetailSkillName}>
                              {a.personaName || 'Main AI'}
                            </span>
                            <button
                              className={styles.packUnassignBtn}
                              style={{ position: 'static', opacity: 1, width: 16, height: 16 }}
                              onClick={() => handleUnassign(a.packId, a.personaId)}
                            >
                              <X size={8} />
                            </button>
                          </div>
                        ))}
                      {assignments.filter((a) => a.packId === selectedPack.id).length === 0 && (
                        <div style={{ fontSize: 12, color: '#64748b', padding: 8 }}>
                          Not assigned to any target
                        </div>
                      )}
                    </div>

                    <button className={styles.btnGhost} onClick={() => openEditPack(selectedPack)}>
                      <Edit3 size={12} style={{ marginRight: 6 }} />
                      Edit Pack
                    </button>
                    <button
                      className={styles.btnDanger}
                      onClick={() => handleDeletePack(selectedPack.id)}
                    >
                      <Trash2 size={12} style={{ marginRight: 6 }} />
                      Delete Pack
                    </button>
                  </>
                )}

                {/* Create / Edit mode */}
                {(panelMode === 'create' || panelMode === 'edit') && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Pack Name</label>
                      <input
                        className={styles.formInput}
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder="e.g. Frontend Design Pack"
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Description</label>
                      <textarea
                        id="skill-pack-description"
                        name="skill-pack-description"
                        className={styles.formTextarea}
                        value={formDesc}
                        onChange={(e) => setFormDesc(e.target.value)}
                        placeholder="What is this pack for?"
                        rows={2}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Icon</label>
                      <div className={styles.iconRow}>
                        {PACK_ICONS.map((icon) => (
                          <div
                            key={icon}
                            className={`${styles.iconOption} ${formIcon === icon ? styles.iconOptionActive : ''}`}
                            onClick={() => setFormIcon(icon)}
                          >
                            {icon}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Color</label>
                      <div className={styles.colorRow}>
                        {PACK_COLORS.map((color) => (
                          <div
                            key={color}
                            className={`${styles.colorSwatch} ${formColor === color ? styles.colorSwatchActive : ''}`}
                            style={{ background: color }}
                            onClick={() => setFormColor(color)}
                          />
                        ))}
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        Skills ({formSelectedSkills.size} selected)
                      </label>
                      <div className={styles.skillList}>
                        {installedSkills.map((skill) => (
                          <div
                            key={skill.fullName}
                            className={styles.skillItem}
                            onClick={() => toggleSkillSelection(skill.fullName)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div
                              className={`${styles.skillItemCheck} ${
                                formSelectedSkills.has(skill.fullName)
                                  ? styles.skillItemChecked
                                  : ''
                              }`}
                            >
                              {formSelectedSkills.has(skill.fullName) && <Check size={10} />}
                            </div>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div className={styles.skillItemName}>{skill.name}</div>
                              <div className={styles.skillItemDesc}>{skill.description}</div>
                            </div>
                          </div>
                        ))}
                        {installedSkills.length === 0 && (
                          <div
                            style={{
                              fontSize: 12,
                              color: '#64748b',
                              textAlign: 'center',
                              padding: 12,
                            }}
                          >
                            No skills installed. Use the search in the left column to add skills.
                          </div>
                        )}
                      </div>
                    </div>

                    {panelMode === 'create' && (
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Assign To</label>
                        <select
                          className={styles.assignSelect}
                          value={assignTarget}
                          onChange={(e) => setAssignTarget(e.target.value)}
                        >
                          {targets.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <button
                      className={styles.btnPrimary}
                      onClick={async () => {
                        if (panelMode === 'create') {
                          await handleCreatePack();
                        } else {
                          await handleUpdatePack();
                        }
                      }}
                      disabled={
                        !formName.trim() ||
                        formSelectedSkills.size === 0 ||
                        createPack.isPending ||
                        updatePack.isPending
                      }
                    >
                      {panelMode === 'create' ? 'Create Pack' : 'Save Changes'}
                    </button>

                    <button
                      className={styles.btnGhost}
                      onClick={() => {
                        setPanelMode('none');
                        setSelectedPack(null);
                      }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.footerHint}>
            Skills &rarr; Packs &rarr; Targets &middot; Hover packs to see connections &middot;
            Click target chips to assign/unassign
          </span>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
};
